import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CircleDollarSign } from 'lucide-react';
import { Card } from '@/components/ui';
import type { OrderSide } from '@/analytics';
import { useSnakeMarketsResolvePriceCube, useTradingOrderPlaceOrder } from '@/lib/endpoints';
import { invalidateTradingCaches } from '@/lib/trading/invalidate-trading-caches';
import { PlaceOrderDtoAction, PlaceOrderDtoSide, PlaceOrderDtoType, SnakeMarketsResolvePriceCubeFormat } from '@/lib/models';
import { centsToPercent } from '@/lib/binary-market';
import { computeBuyQuote } from '@/components/features/BinaryMarkets/compute-buy-quote';
import { useTerritory } from '@/lib/territory/useTerritory';
import { useOpenTerritoryGate } from '@/store/territoryGateStore';
import { useOnboardingGate } from '@/lib/auth/useOnboardingGate';
import { useCapabilityEnabled, KILL_SWITCH } from '@/lib/launchdarkly/killSwitches';
import { ORDER_FAILURE_MESSAGE, toOrderFailureReason } from '@/lib/trading/order-failure';

export interface SnakeBuyPanelProps {
  /** Active instrument (for analytics attribution). */
  instrument: string;
  /** Active resolution sub-category code. */
  subCategoryCode: string;
  /**
   * Question recap rendered on top of the card at lg+ (Figma node 6672:170285)
   * — the desktop trade rail restates the market beside the chart. Omitted on
   * the phone drawer / tablet inline panel, where the header sits right above.
   */
  question?: {
    imageUrl?: string | null;
    title: string;
    window: string;
  };
  /** Outcome being traded — the series' single strike outcome. Empty disables buying. */
  outcomeId: string;
  /** Market id of that outcome — analytics + cache scoping. */
  marketId: string;
  /** Live Up price in cents (0–100), or `null` until known. */
  upCents: number | null;
  /** Live Down price in cents (0–100), or `null` until known. */
  downCents: number | null;
}

/**
 * Snake buy panel (ARC-84 §6, Figma node 1659-67773).
 *
 * "Set Amount" stepper (−/+ by $1, min $0), the admin-configured dollar quick-add
 * cubes (`/snake/price-cubes`), and the Up/Down gradient buttons — each showing
 * its side's live chance % with a "Payout $X.XX" line. Tapping Up/Down places a
 * **buy** order on the strike outcome (`side` yes/no, the dollar amount, the live
 * side price in cents) via {@link useTradingOrderPlaceOrder}, then refetches the
 * order + position mirrors. Gated by the order-placement kill-switch and the
 * territory gate (the server enforces both as backstops).
 */
export function SnakeBuyPanel({ outcomeId, upCents, downCents, question }: SnakeBuyPanelProps) {
  const queryClient = useQueryClient();
  const territory = useTerritory();
  const openTerritoryGate = useOpenTerritoryGate();
  const ensureOrderAuth = useOnboardingGate('order_gate');
  const tradingEnabled = useCapabilityEnabled(KILL_SWITCH.TRADING_ORDER_PLACEMENT);
  const [amount, setAmount] = useState(1);
  const [submittingSide, setSubmittingSide] = useState<OrderSide | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Dollars quick-add cubes — admin-configured (§6 / snake/price-cubes).
  const { data: cubeData } = useSnakeMarketsResolvePriceCube({ format: SnakeMarketsResolvePriceCubeFormat.DOLLARS });
  const cubes = cubeData?.values ?? [];

  const upQuote = computeBuyQuote(amount, 'dollars', upCents);
  const downQuote = computeBuyQuote(amount, 'dollars', downCents);
  const upPct = centsToPercent(upCents);
  const downPct = centsToPercent(downCents);

  const placeOrder = useTradingOrderPlaceOrder({
    mutation: {
      onSuccess: (result) => {
        if (!result.success) {
          const reason = toOrderFailureReason(result.errorCode);
          setError(ORDER_FAILURE_MESSAGE[reason]);
          return;
        }
        // Instant feedback: refetch the trading mirrors so the new order and any
        // resulting position appear. (Realtime sync also covers cross-device.)
        invalidateTradingCaches(queryClient);
      },
      onError: () => {
        setError(ORDER_FAILURE_MESSAGE.network);
      },
      onSettled: () => setSubmittingSide(null),
    },
  });

  function step(delta: number) {
    setAmount((prev) => Math.max(0, prev + delta));
  }

  function handleCube(value: number) {
    setAmount((prev) => prev + value);
  }

  /** Whether a given side can be bought right now (price known + not gated/in-flight). */
  function canBuy(priceCents: number | null): boolean {
    return tradingEnabled && Boolean(outcomeId) && amount > 0 && priceCents != null && submittingSide == null;
  }

  function handleBuy(side: OrderSide, priceCents: number | null) {
    if (!canBuy(priceCents) || priceCents == null) return;
    // Signup-funnel resume comes before any verification gate (ARC-145).
    if (!ensureOrderAuth()) return;
    // Territory gate (proactive); the server also rejects with 403 TERRITORY_BLOCKED.
    if (territory?.blocked) {
      openTerritoryGate({ reason: territory.reason });
      return;
    }
    setError(null);
    setSubmittingSide(side);
    // "Buy now" = market order by dollars (type=market + amount, no price);
    // omitting `type` defaults to a limit resting at priceCents. priceCents is
    // kept above only for the chance/payout display + buy guard.
    placeOrder.mutate({
      data: {
        outcomeId,
        side: side === 'yes' ? PlaceOrderDtoSide.yes : PlaceOrderDtoSide.no,
        action: PlaceOrderDtoAction.buy,
        type: PlaceOrderDtoType.market,
        amount,
      },
    });
  }

  return (
    <Card className="flex flex-col items-center gap-4 p-4 lg:gap-4 lg:rounded-[16px] lg:border-[1.5px] lg:border-stroke-soft lg:p-6">
      {question && (
        <>
          {/* Question recap — desktop rail only (Figma node 6672:170285). */}
          <div className="hidden w-full min-w-0 items-center gap-2 lg:flex">
            {question.imageUrl ? (
              <img src={question.imageUrl} alt="" className="size-[50px] shrink-0 rounded-full object-cover" />
            ) : (
              <div className="size-[50px] shrink-0 rounded-full bg-stroke-soft" />
            )}
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-[20px] font-medium text-ink">{question.title}</span>
              <span className="truncate text-[16px] tracking-[-0.32px] text-ink">{question.window}</span>
            </div>
          </div>
          <span className="hidden h-px w-full shrink-0 bg-stroke-soft lg:block" />
        </>
      )}

      {/* Centered title on the phone drawer; left-aligned card header at lg. */}
      <span className="text-[20px] font-medium text-ink lg:w-full lg:text-left lg:text-[18px]">Set Amount</span>

      {/* Stepper row */}
      <div className="flex w-full max-w-[280px] items-center justify-between rounded-[43px] border-[1.5px] border-stroke-soft bg-input-soft px-5 py-2">
        <button
          type="button"
          aria-label="Decrease amount"
          onClick={() => step(-1)}
          className="cursor-pointer border-none bg-transparent px-2 text-[32px] leading-none text-ink"
        >
          −
        </button>
        <span className="text-[26px] font-semibold tabular-nums text-ink">${amount}</span>
        <button
          type="button"
          aria-label="Increase amount"
          onClick={() => step(1)}
          className="cursor-pointer border-none bg-transparent px-2 text-[32px] leading-none text-ink"
        >
          +
        </button>
      </div>

      {/* Quick-add cubes (§6 / snake/price-cubes) — equal-width pills that share
          the stepper's row width (Figma node 6672:170149). */}
      <div className="flex w-full max-w-[292px] flex-wrap items-center justify-center gap-2">
        {cubes.map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => handleCube(value)}
            className="h-[30px] min-w-0 flex-1 basis-[56px] whitespace-nowrap rounded-[61px] border-[1.5px] border-stroke-soft bg-surface px-2 text-[18px] font-medium uppercase text-ink transition-colors hover:bg-surface-soft active:opacity-70 lg:h-[36px] lg:text-[20px]"
          >
            + {value}
          </button>
        ))}
      </div>

      {/* Up / Down buy buttons */}
      <div className="flex w-full gap-3">
        <SideButton
          label="Up"
          percent={upPct}
          payout={upQuote.toWinDollars}
          side="up"
          disabled={!canBuy(upCents)}
          pending={submittingSide === 'yes'}
          onClick={() => handleBuy('yes', upCents)}
        />
        <SideButton
          label="Down"
          percent={downPct}
          payout={downQuote.toWinDollars}
          side="down"
          disabled={!canBuy(downCents)}
          pending={submittingSide === 'no'}
          onClick={() => handleBuy('no', downCents)}
        />
      </div>

      {!tradingEnabled && (
        <p className="text-center text-[13px] font-medium text-ink-muted">Trading is temporarily paused.</p>
      )}
      {error && <p className="text-center text-[13px] font-medium text-danger-alt">{error}</p>}

      {/* Consent recap (Figma node 6672:170155). Placeholder routes shared with
          the signup terms step — swap for canonical CDNA URLs once Legal
          supplies them. */}
      <p className="m-0 w-full text-center text-[12px] leading-normal tracking-[-0.24px] text-ink-muted">
        By trading, you agree to the{' '}
        <a href="/terms" className="text-inherit underline">
          Operator Terms of Use
        </a>
        ,{' '}
        <a href="/cdna-terms-of-use" className="text-inherit underline">
          CDNA Terms of Use
        </a>{' '}
        &amp;{' '}
        <a href="/cdna-privacy-policy" className="text-inherit underline">
          Privacy Policy
        </a>{' '}
        and{' '}
        <a href="/cdna-rules" className="text-inherit underline">
          CDNA Rules
        </a>
      </p>
    </Card>
  );
}

/** One side of the buy panel — solid green/red pill with the chance % inline
 *  (Figma node 6842:188150), payout line with icon below. */
function SideButton({
  label,
  percent,
  payout,
  side,
  disabled,
  pending,
  onClick,
}: {
  label: string;
  percent: number | null;
  payout: number;
  side: 'up' | 'down';
  disabled: boolean;
  pending: boolean;
  onClick: () => void;
}) {
  return (
    <div className="flex flex-1 flex-col items-center gap-2">
      <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        className={`${side === 'up' ? 'bg-up' : 'bg-down'} flex h-[48px] w-full items-center justify-center rounded-[100px] text-white transition-opacity hover:opacity-90 active:opacity-80 disabled:cursor-not-allowed disabled:opacity-50`}
      >
        <span className="text-[20px] font-semibold capitalize tracking-[-0.4px]">
          {pending ? 'Placing…' : `${label} ${percent == null ? '—' : `${percent}%`}`}
        </span>
      </button>
      <span className="flex items-center gap-1 text-[14px] capitalize text-ink-muted">
        <CircleDollarSign className="size-[18px]" aria-hidden="true" />
        Payout ${payout.toFixed(2)}
      </span>
    </div>
  );
}
